import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function TermsConditions() {
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                +1 (929) 268-8770
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-medicine-bottle-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-600">PillsForU</h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </Link>

            <div className="flex items-center space-x-6">
              <Link to="/cart" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Terms &amp; Conditions</h1>
          <p className="text-xl text-blue-100">Last Updated: January 2024</p>
        </div>
      </div>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              Welcome to PillsForU. By accessing and using our website and services, you agree to be bound by these Terms and Conditions. Please read them carefully before placing an order or using our services.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">1. Acceptance of Terms</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            By accessing, browsing, or using the PillsForU website (pillsforu.com), you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy. If you do not agree with any part of these terms, you must not use our website or services.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">2. Eligibility</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            You must be at least 18 years of age to use our services and purchase products from our website. By using our services, you represent and warrant that:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>You are at least 18 years old</li>
            <li>You have the legal capacity to enter into binding contracts</li>
            <li>All information you provide is accurate, current, and complete</li>
            <li>You will use our services only for lawful purposes</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">3. Products and Services</h2>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Product Information</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We strive to provide accurate product descriptions, images, and pricing information. However, we do not warrant that product descriptions, images, or other content on our website are accurate, complete, reliable, current, or error-free. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update information at any time without prior notice.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Prescription Medications</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Certain products available on our website require a valid prescription from a licensed healthcare provider. By ordering prescription medications, you confirm that:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>You have a valid prescription for the medication</li>
            <li>The medication is for your personal use only</li>
            <li>You will provide prescription verification when requested</li>
            <li>You understand the risks and benefits of the medication</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Product Availability</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            All products are subject to availability. We reserve the right to limit quantities, discontinue products, or refuse orders at our discretion. If a product becomes unavailable after you place an order, we will notify you and offer a refund or substitute product.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">4. Ordering and Payment</h2>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Order Placement</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            When you place an order through our website, you are making an offer to purchase the products. We reserve the right to accept or decline your order for any reason. Order confirmation does not constitute acceptance of your order. Acceptance occurs when we ship the products to you.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Pricing</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            All prices are listed in US Dollars (USD) and are subject to change without notice. We reserve the right to correct pricing errors. If we discover a pricing error after you have placed an order, we will notify you and give you the option to confirm the order at the correct price or cancel the order for a full refund.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Payment Methods</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We accept major credit cards, debit cards, and other payment methods as displayed on our website. By providing payment information, you represent and warrant that you are authorized to use the payment method. All payments are processed through secure, encrypted payment gateways.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">5. Shipping and Delivery</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We offer worldwide shipping to most countries. Shipping times and costs vary depending on your location and the shipping method selected. Standard international shipping typically takes 7-15 business days. We are not responsible for delays caused by customs, weather, or other factors beyond our control.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Risk of loss and title for products pass to you upon delivery to the shipping carrier. You are responsible for providing accurate shipping information. We are not liable for orders shipped to incorrect addresses provided by you.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">6. Returns and Refunds</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Due to the nature of pharmaceutical products and health regulations, we have specific return and refund policies:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Unopened products in original packaging may be returned within 30 days of delivery</li>
            <li>Opened or used products cannot be returned for health and safety reasons</li>
            <li>Damaged or defective products will be replaced or refunded at no cost to you</li>
            <li>Wrong items shipped will be corrected at our expense</li>
            <li>Refunds are processed within 7-10 business days after we receive the returned product</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed">
            For detailed information, please refer to our Refund Policy page.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">7. User Accounts</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            When you create an account on our website, you are responsible for:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
            <li>Ensuring your account information is accurate and up-to-date</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We reserve the right to suspend or terminate accounts that violate these Terms and Conditions or engage in fraudulent or illegal activities.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">8. Intellectual Property</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            All content on the PillsForU website, including text, graphics, logos, images, software, and other materials, is the property of PillsForU or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Copy, reproduce, distribute, or create derivative works from our content</li>
            <li>Use our trademarks, logos, or brand names without written permission</li>
            <li>Frame or mirror any part of our website</li>
            <li>Use automated systems to access or collect data from our website</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">9. Prohibited Uses</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            You agree not to use our website or services for any unlawful or prohibited purpose, including but not limited to:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Violating any local, state, national, or international law</li>
            <li>Purchasing medications for resale or distribution</li>
            <li>Providing false or misleading information</li>
            <li>Impersonating another person or entity</li>
            <li>Interfering with or disrupting our website or servers</li>
            <li>Attempting to gain unauthorized access to our systems</li>
            <li>Transmitting viruses, malware, or other harmful code</li>
            <li>Harassing, threatening, or abusing other users or our staff</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">10. Disclaimer of Warranties</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Warranties of merchantability and fitness for a particular purpose</li>
            <li>Warranties that our website will be uninterrupted, error-free, or secure</li>
            <li>Warranties regarding the accuracy or reliability of information on our website</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We do not provide medical advice. Always consult with a qualified healthcare professional before starting any medication or treatment.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">11. Limitation of Liability</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, PILLSFORU SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF OUR WEBSITE OR SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed">
            OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM YOUR USE OF OUR SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE PRODUCTS OR SERVICES IN QUESTION.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">12. Indemnification</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            You agree to indemnify, defend, and hold harmless PillsForU, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Your use of our website or services</li>
            <li>Your violation of these Terms and Conditions</li>
            <li>Your violation of any rights of another party</li>
            <li>Your provision of false or misleading information</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">13. Governing Law and Dispute Resolution</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            These Terms and Conditions shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising from these terms or your use of our services shall be resolved through binding arbitration, except where prohibited by law.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">14. Changes to Terms</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes your acceptance of the modified terms. We encourage you to review these terms periodically.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">15. Severability</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            If any provision of these Terms and Conditions is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">16. Contact Information</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            If you have any questions or concerns about these Terms and Conditions, please contact us:
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <p className="text-gray-700 mb-2"><strong>Email:</strong> sam4104t@gmail.com</p>
            <p className="text-gray-700 mb-2"><strong>Phone:</strong> +1 (929) 268-8770</p>
            <p className="text-gray-700"><strong>Website:</strong> pillsforu.com</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mt-8">
            <p className="text-gray-700 leading-relaxed">
              <strong>By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About Us</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p>Phone: +1 (929) 268-8770</p>
              <p>Email: sam4104t@gmail.com</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2024 PillsForU.com. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}